// DealSmoke.jsx: M2.a-2 in-browser smoke for State:deal().
//
// Surfaces the rules engine output in the DOM so the deal can be
// visually verified in a real browser (not just Node tests). NOT M2.c UI
//, text-only, no card sprites, no animations. Intentionally minimal so
// it doesn't drift into UI scope before M2.c-1 is planned.
//
// Validation goal: prove that the same cards.lua + state.lua + serialize.lua
// running in browser wasmoon produces the same deal as Node (parity contract
// the M2.a-2 deal_fixture golden enforces in CI).

import { useEffect, useState } from 'preact/hooks';
import { createLuaRuntime } from './lua/runtime.js';
import cardsSrc from './lua/game/cards.lua?raw';
import stateSrc from './lua/game/state.lua?raw';
import serializeSrc from './lua/parity/serialize.lua?raw';

const SEEDS = [42, 7, 12345, 99999];

export function DealSmoke() {
  const [status, setStatus] = useState('init…');
  const [engine, setEngine] = useState(/** @type {any} */ (null));
  const [seed, setSeed] = useState(42);
  const [view, setView] = useState(/** @type {any} */ (null));

  useEffect(() => {
    let cancelled = false;
    let eng;
    (async () => {
      try {
        const runtime = await createLuaRuntime();
        if (cancelled) {
          await runtime.engine.global.close();
          return;
        }
        eng = runtime.engine;
        await eng.doString(`
          package.loaded['parity.serialize'] = (function() ${serializeSrc} end)()
          package.loaded['game.cards']       = (function() ${cardsSrc} end)()
          State = (function() ${stateSrc} end)()
        `);
        setEngine(eng);
        setStatus('ready');
      } catch (err) {
        if (!cancelled) setStatus(`init failed: ${/** @type {Error} */ (err).message}`);
        console.error('[deal-smoke]', err);
      }
    })();
    return () => {
      cancelled = true;
      if (eng) eng.global.close();
    };
  }, []);

  useEffect(() => {
    if (!engine) return;
    (async () => {
      try {
        // Fresh state per seed change. Pull internal hands + kitty for the
        // smoke view; this is dev-only inspection, not the snapshot wire format.
        await engine.doString(`s = State.new(); s:deal(${seed})`);
        const s = engine.global.get('s');
        const hands = Object.values(s._private.hands).map((h) => Object.values(h));
        const kitty = Object.values(s._private.kitty);
        setView({
          seed,
          current_state: s._public.current_state,
          up_card: s._public.up_card,
          hands,
          kitty,
        });
      } catch (err) {
        setStatus(`deal failed: ${/** @type {Error} */ (err).message}`);
        console.error('[deal-smoke]', err);
      }
    })();
  }, [engine, seed]);

  if (!view) return <div class="deal-smoke">{status}</div>;

  return (
    <div class="deal-smoke">
      <div class="ds-header">
        <strong>M2.a-2 deal smoke</strong>
        <span class="ds-state">state: {view.current_state}</span>
        <span class="ds-up">up_card: <code>{view.up_card}</code></span>
      </div>
      <div class="ds-controls">
        <span>seed:</span>
        {SEEDS.map((s) => (
          <button
            key={s}
            type="button"
            class={s === seed ? 'ds-seed active' : 'ds-seed'}
            onClick={() => setSeed(s)}
          >
            {s}
          </button>
        ))}
      </div>
      <div class="ds-table">
        {view.hands.map((hand, i) => (
          <div class="ds-seat" key={i}>
            <div class="ds-seat-label">Seat {i + 1}</div>
            <div class="ds-cards">
              {hand.map((c, j) => <code class="ds-card" key={`${i}-${j}-${c}`}>{c}</code>)}
            </div>
          </div>
        ))}
        <div class="ds-seat ds-kitty">
          <div class="ds-seat-label">Kitty (hidden in prod)</div>
          <div class="ds-cards">
            {view.kitty.map((c, j) => <code class="ds-card" key={`k-${j}-${c}`}>{c}</code>)}
          </div>
        </div>
      </div>
    </div>
  );
}
